import React, { useState, useContext, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSync,
  faCheckCircle,
  faExclamationCircle,
  faFilter,
  faTimes,
  faSpinner,
  faBuilding,
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { DataContext } from "../contexts/DataContext";
import Select from "react-select";
import { getTranslation } from "../utils/i18n";

/**
 * DataRefreshManager - A component to force refresh data from the backend
 * with specific customer numbers.
 */
const DataRefreshManager = () => {
  const { customerNumbers, isAdmin } = useAuth();
  const { setShouldFetchData } = useContext(DataContext);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [customCustomerInput, setCustomCustomerInput] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [status, setStatus] = useState({ type: null, message: "" });
  const [showPanel, setShowPanel] = useState(false);
  const [language, setLanguage] = useState("en");
  const [customerOptions, setCustomerOptions] = useState([]);
  const [refreshType, setRefreshType] = useState("both"); // "traffic", "catalogue", or "both"

  // Create customer options based on authorized customer numbers
  useEffect(() => {
    if (customerNumbers && customerNumbers.length > 0) {
      const options = customerNumbers.map((num) => ({
        value: num,
        label: num,
      }));
      setCustomerOptions(options);
    }
  }, [customerNumbers]);

  // Parse custom customer input (comma-separated)
  const handleCustomCustomerInputChange = (e) => {
    setCustomCustomerInput(e.target.value);
  };

  // Allow alphanumeric customer IDs (e.g. "EC004", "EC-004") plus commas/whitespace.
  const isValidCustomerInput = (input) => {
    return /^[A-Za-z0-9,\s-]*$/.test(input);
  };

  // Parse the custom input into an array of customer IDs (strings).
  const parseCustomCustomerInput = () => {
    if (!customCustomerInput.trim()) return [];

    return customCustomerInput
      .split(",")
      .map((num) => num.trim())
      .filter((num) => num.length > 0);
  };

  // Combine selected customers from dropdown and custom input
  const getAllSelectedCustomers = () => {
    const fromDropdown = selectedCustomers.map((option) => option.value);
    const fromCustomInput = parseCustomCustomerInput();

    // Combine and remove duplicates
    return [...new Set([...fromDropdown, ...fromCustomInput])];
  };

  // Refresh traffic data with the selected customers
  const refreshTrafficData = async (customersToRefresh) => {
    setStatus({ type: "loading", message: "Refreshing traffic data..." });
    try {
      // Convert to strings to support both numeric and alphanumeric customer IDs
      const formattedCustomerNumbers = customersToRefresh.map((num) =>
        num.toString().trim()
      );

      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_API_URL}/api/traffic`,
        {
          params: {
            customer_id:
              formattedCustomerNumbers.length > 0
                ? formattedCustomerNumbers
                : [],
            refresh: true,
          },
        }
      );

      // Update the context to signal data has been refreshed
      setShouldFetchData((prev) => ({ ...prev, fetchTrafficData: true }));

      return {
        success: true,
        message: `Successfully refreshed traffic data for ${customersToRefresh.length} customer(s)`,
      };
    } catch (error) {
      console.error("Error refreshing traffic data:", error);
      return {
        success: false,
        message: `Error refreshing traffic data: ${error.message}`,
      };
    }
  };

  // Refresh catalogue data with the selected customers
  const refreshCatalogueData = async (customersToRefresh) => {
    setStatus({ type: "loading", message: "Refreshing catalogue data..." });
    try {
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        return {
          success: false,
          message: "Access token is not available",
        };
      }

      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_API_URL}/api/customer_item_catalogue/getMasterDatabase`,
        {
          params: {
            customer_key: customersToRefresh,
            refresh: true,
          },
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      return {
        success: true,
        message: `Successfully refreshed catalogue data for ${customersToRefresh.length} customer(s)`,
      };
    } catch (error) {
      console.error("Error refreshing catalogue data:", error);
      return {
        success: false,
        message: `Error refreshing catalogue data: ${error.message}`,
      };
    }
  };

  // Main refresh handler
  const handleRefresh = async () => {
    setRefreshing(true);
    setStatus({ type: "loading", message: "Preparing refresh..." });

    const customersToRefresh = getAllSelectedCustomers();

    if (customersToRefresh.length === 0) {
      setStatus({
        type: "error",
        message: "Please select at least one customer number",
      });
      setRefreshing(false);
      return;
    }

    try {
      let trafficResult = { success: true };
      let catalogueResult = { success: true };

      // Refresh based on selected type
      if (refreshType === "traffic" || refreshType === "both") {
        trafficResult = await refreshTrafficData(customersToRefresh);
      }

      if (refreshType === "catalogue" || refreshType === "both") {
        catalogueResult = await refreshCatalogueData(customersToRefresh);
      }

      // Determine overall status
      if (trafficResult.success && catalogueResult.success) {
        setStatus({
          type: "success",
          message: "Successfully refreshed all selected data",
        });
      } else {
        const errorMessages = [];
        if (!trafficResult.success) errorMessages.push(trafficResult.message);
        if (!catalogueResult.success)
          errorMessages.push(catalogueResult.message);

        setStatus({
          type: "error",
          message: errorMessages.join(". "),
        });
      }
    } catch (error) {
      console.error("Refresh error:", error);
      setStatus({
        type: "error",
        message: `An unexpected error occurred: ${error.message}`,
      });
    } finally {
      setRefreshing(false);

      // Auto-hide status after 5 seconds on success
      if (status.type === "success") {
        setTimeout(() => {
          setStatus({ type: null, message: "" });
        }, 5000);
      }
    }
  };

  // Clear all selections
  const clearSelections = () => {
    setSelectedCustomers([]);
    setCustomCustomerInput("");
    setStatus({ type: null, message: "" });
  };

  // Render status indicator
  const renderStatusIndicator = () => {
    if (!status.type) return null;

    let icon, colorClass;

    switch (status.type) {
      case "success":
        icon = faCheckCircle;
        colorClass = "text-green-500";
        break;
      case "error":
        icon = faExclamationCircle;
        colorClass = "text-red-500";
        break;
      case "loading":
        icon = faSpinner;
        colorClass = "text-blue-500 animate-spin";
        break;
      default:
        return null;
    }

    return (
      <div className={`flex items-center ${colorClass} mt-2`}>
        <FontAwesomeIcon icon={icon} className="mr-2" />
        <span>{status.message}</span>
      </div>
    );
  };

  // Only show for admin users
  if (!isAdmin) {
    return null;
  }

  return (
    <div className="w-full bg-white rounded-lg shadow-sm p-5 mb-6 border border-gray-300">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-gray-800">
          <FontAwesomeIcon icon={faSync} className="mr-2 text-blue-600" />
          {getTranslation(language, "dataRefreshManager") ||
            "Data Refresh Manager"}
        </h2>
        <FontAwesomeIcon
          icon={showPanel ? faTimes : faFilter}
          onClick={() => setShowPanel(!showPanel)}
          className="cursor-pointer text-gray-700 hover:text-blue-600"
        />
      </div>

      {showPanel && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-4">
            <div className="filter-group">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <FontAwesomeIcon
                  icon={faBuilding}
                  className="mr-1 text-blue-600"
                />
                {getTranslation(language, "selectCustomers") ||
                  "Select Customers"}
              </label>
              <Select
                isMulti
                name="customers"
                options={customerOptions}
                className="basic-multi-select"
                classNamePrefix="select-filter"
                onChange={setSelectedCustomers}
                value={selectedCustomers}
                placeholder={
                  getTranslation(language, "selectCustomersPlaceholder") ||
                  "Select customers to refresh"
                }
                styles={{
                  control: (base) => ({
                    ...base,
                    borderColor: "#e2e8f0",
                    boxShadow: "none",
                    "&:hover": {
                      borderColor: "#cbd5e1",
                    },
                  }),
                  multiValue: (base) => ({
                    ...base,
                    backgroundColor: "#e1effe",
                  }),
                  multiValueLabel: (base) => ({
                    ...base,
                    color: "#1e40af",
                  }),
                  multiValueRemove: (base) => ({
                    ...base,
                    color: "#60a5fa",
                    ":hover": {
                      backgroundColor: "#bfdbfe",
                      color: "#1e3a8a",
                    },
                  }),
                }}
              />
            </div>

            <div className="filter-group">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <FontAwesomeIcon
                  icon={faBuilding}
                  className="mr-1 text-blue-600"
                />
                {getTranslation(language, "customCustomers") ||
                  "Custom Customer Numbers"}
              </label>
              <input
                type="text"
                value={customCustomerInput}
                onChange={handleCustomCustomerInputChange}
                placeholder={
                  getTranslation(language, "customCustomersPlaceholder") ||
                  "Enter comma-separated customer numbers"
                }
                className={`w-full px-3 py-2 border rounded-md text-sm ${
                  !isValidCustomerInput(customCustomerInput)
                    ? "border-red-500"
                    : "border-gray-300"
                } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
              />
              {!isValidCustomerInput(customCustomerInput) && (
                <p className="mt-1 text-red-500 text-xs">
                  {getTranslation(language, "customCustomersError") ||
                    "Please enter only numbers and commas"}
                </p>
              )}
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {getTranslation(language, "refreshType") || "Refresh Type"}
            </label>
            <div className="flex space-x-4">
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  className="form-radio text-blue-600"
                  name="refreshType"
                  value="both"
                  checked={refreshType === "both"}
                  onChange={() => setRefreshType("both")}
                />
                <span className="ml-2 text-sm text-gray-700">
                  {getTranslation(language, "refreshBoth") ||
                    "Both Traffic & Catalogue"}
                </span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  className="form-radio text-blue-600"
                  name="refreshType"
                  value="traffic"
                  checked={refreshType === "traffic"}
                  onChange={() => setRefreshType("traffic")}
                />
                <span className="ml-2 text-sm text-gray-700">
                  {getTranslation(language, "refreshTraffic") || "Traffic Only"}
                </span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  className="form-radio text-blue-600"
                  name="refreshType"
                  value="catalogue"
                  checked={refreshType === "catalogue"}
                  onChange={() => setRefreshType("catalogue")}
                />
                <span className="ml-2 text-sm text-gray-700">
                  {getTranslation(language, "refreshCatalogue") ||
                    "Catalogue Only"}
                </span>
              </label>
            </div>
          </div>

          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0 w-full md:w-auto">
              {renderStatusIndicator()}
            </div>

            <div className="flex space-x-4">
              <button
                onClick={clearSelections}
                disabled={refreshing}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50 transition"
              >
                {getTranslation(language, "clear") || "Clear"}
              </button>
              <button
                onClick={handleRefresh}
                disabled={
                  refreshing || !isValidCustomerInput(customCustomerInput)
                }
                className={`px-4 py-2 ${
                  refreshing ? "bg-blue-400" : "bg-blue-600 hover:bg-blue-700"
                } text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition flex items-center justify-center`}
              >
                {refreshing && (
                  <FontAwesomeIcon
                    icon={faSpinner}
                    className="animate-spin mr-2"
                  />
                )}
                {refreshing
                  ? getTranslation(language, "refreshing") || "Refreshing..."
                  : getTranslation(language, "refreshData") || "Refresh Data"}
              </button>
            </div>
          </div>

          {/* Display currently selected customers */}
          {(selectedCustomers.length > 0 ||
            parseCustomCustomerInput().length > 0) && (
            <div className="mt-4 text-sm text-gray-600">
              <div className="flex items-center flex-wrap">
                <span className="mr-2">
                  {getTranslation(language, "selectedCustomers") ||
                    "Selected customers:"}
                  :
                </span>
                <div className="flex flex-wrap gap-2 mt-2">
                  {selectedCustomers.map((item) => (
                    <span
                      key={item.value}
                      className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center"
                    >
                      {item.label}
                      <button
                        onClick={() =>
                          setSelectedCustomers((prev) =>
                            prev.filter((i) => i.value !== item.value)
                          )
                        }
                        className="ml-1 text-blue-500 hover:text-blue-700"
                      >
                        <FontAwesomeIcon icon={faTimes} className="text-xs" />
                      </button>
                    </span>
                  ))}

                  {parseCustomCustomerInput().map((num) => (
                    <span
                      key={`custom-${num}`}
                      className="bg-green-50 text-green-700 px-2 py-1 rounded-full flex items-center"
                    >
                      {num} (custom)
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DataRefreshManager;
